import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './utils/authUtils';

const BACKEND_URL = API_URL;

// Helper function to construct full S3 URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  const S3_BASE_URL = 'https://scholars-uploads.s3.us-east-2.amazonaws.com';
  return `${S3_BASE_URL}/${imagePath}`;
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const RecruiterInteractionHistory = () => {
  const { recruiterId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyData, setHistoryData] = useState(null);
  const [expandedCycles, setExpandedCycles] = useState(new Set());
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  // Resume packet export (current cycle): per-category job state + poll intervals
  const [packetEnabled, setPacketEnabled] = useState(true);
  const [packetJobs, setPacketJobs] = useState({}); // category -> job object
  const packetPollRef = useRef({});

  // Determine viewing mode (admin, board liaison, or recruiter)
  const isAdminMode = localStorage.getItem('token') && !localStorage.getItem('recruiterInfo');
  const isBoardLiaisonMode = localStorage.getItem('boardLiaisonToken') && localStorage.getItem('boardLiaisonInfo');

  // Handle back to dashboard navigation based on viewing mode
  const handleBackToDashboard = () => {
    if (isAdminMode) {
      // Admin viewing mode - go back to admin panel
      navigate('/admin');
    } else if (isBoardLiaisonMode) {
      // Board liaison viewing mode - go back to their dashboard
      navigate('/board-liaison/dashboard');
    } else {
      // Regular recruiter mode
      navigate('/recruiter/dashboard');
    }
  };

  useEffect(() => {
    fetchInteractionHistory();
  }, [recruiterId]);

  const fetchInteractionHistory = async () => {
    try {
      setLoading(true);
      
      // Get authentication token
      const token = localStorage.getItem('recruiterToken') || localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token found');
        navigate('/recruiter/login');
        return;
      }
      
      const response = await axios.get(
        `${BACKEND_URL}/api/recruiter/interaction-history/${recruiterId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setHistoryData(response.data);
      
      // Expand current cycle by default
      if (response.data.cycles.length > 0) {
        const currentCycle = response.data.cycles.find(cycle => cycle.is_current);
        if (currentCycle) {
          setExpandedCycles(new Set([currentCycle.year]));
        }
      }
      
    } catch (err) {
      console.error('Error fetching interaction history:', err);
      
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.error('Authentication failed, redirecting to login');
        localStorage.removeItem('recruiterToken');
        localStorage.removeItem('recruiterInfo');
        localStorage.removeItem('token');
        navigate('/recruiter/login');
      } else {
        setError('Failed to load interaction history');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCycleExpansion = (year) => {
    const newExpanded = new Set(expandedCycles);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedCycles(newExpanded);
  };

  const toggleStudentExpansion = (studentId) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
    } else {
      newExpanded.add(studentId);
    }
    setExpandedStudents(newExpanded);
  };

  const packetToken = () => localStorage.getItem('recruiterToken') || localStorage.getItem('token');

  // Check whether the resume-packet feature is enabled (default ON), and clean
  // up any poll intervals on unmount.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/settings/recruiter_packet_export_enabled/public`);
        setPacketEnabled(res.data.setting_value !== 'false');
      } catch (e) {
        setPacketEnabled(true); // default ON
      }
    })();
    const polls = packetPollRef.current;
    return () => {
      Object.values(polls).forEach((id) => clearInterval(id));
    };
  }, []);

  const pollPacket = (category, jobId) => {
    if (packetPollRef.current[category]) clearInterval(packetPollRef.current[category]);
    packetPollRef.current[category] = setInterval(async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/recruiter/packet/status/${jobId}`,
          { headers: { Authorization: `Bearer ${packetToken()}` } }
        );
        const job = res.data;
        setPacketJobs((prev) => ({ ...prev, [category]: job }));
        if (job.status === 'done' || job.status === 'error') {
          clearInterval(packetPollRef.current[category]);
          delete packetPollRef.current[category];
        }
      } catch (err) {
        console.error('Error polling packet:', err);
        clearInterval(packetPollRef.current[category]);
        delete packetPollRef.current[category];
        setPacketJobs((prev) => ({ ...prev, [category]: { status: 'error', error: 'Status check failed' } }));
      }
    }, 3000);
  };

  const startPacket = async (category) => {
    setPacketJobs((prev) => ({ ...prev, [category]: { status: 'running', resumes_merged: 0 } }));
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/recruiter/packet/start`,
        { recruiter_id: parseInt(recruiterId, 10), category },
        { headers: { Authorization: `Bearer ${packetToken()}` } }
      );
      const job = res.data;
      setPacketJobs((prev) => ({ ...prev, [category]: job }));
      pollPacket(category, job.job_id);
    } catch (err) {
      console.error('Error starting packet:', err);
      const msg = err.response?.status === 403 ? 'Packet export is disabled' : 'Failed to start packet export';
      setPacketJobs((prev) => ({ ...prev, [category]: { status: 'error', error: msg } }));
    }
  };

  const exportToCSV = () => {
    if (!historyData || historyData.cycles.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare CSV data
    const csvData = [];
    
    // CSV Headers
    csvData.push([
      'Cycle Year',
      'Category',
      'Student ID',
      'Student Name',
      'University',
      'Major',
      'Class Year',
      'GPA',
      'Data Source',
      'Email',
      'Personal Email',
      'Shortlisted Date',
      'Profile Request Sent',
      'Profile Request Read',
      'External Link Sent',
      'External Link Read',
      'Interview Invite Sent',
      'Interview Invite Read',
      'Application Date',
      'Offer Sent',
      'Offer Status',
      'Offer Response Date'
    ]);

    // Add data rows
    historyData.cycles.forEach(cycle => {
      // Favorited You students
      cycle.interested_candidates.forEach(candidate => {
        csvData.push([
          cycle.year,
          'Favorited You',
          candidate.id,
          candidate.first_name && candidate.last_name ? `${candidate.first_name} ${candidate.last_name}` : `Candidate #${candidate.id}`,
          candidate.university || '',
          candidate.major || '',
          candidate.class_year || '',
          candidate.gpa || '',
          candidate.data_source || '',
          candidate.email || '',
          candidate.personal_email || '',
          candidate.shortlisted_at ? new Date(candidate.shortlisted_at).toLocaleDateString() : '',
          candidate.apply_invite_sent_at ? new Date(candidate.apply_invite_sent_at).toLocaleDateString() : '',
          candidate.apply_invite_read_at ? new Date(candidate.apply_invite_read_at).toLocaleDateString() : '',
          candidate.external_link_sent_at ? new Date(candidate.external_link_sent_at).toLocaleDateString() : '',
          candidate.external_link_read_at ? new Date(candidate.external_link_read_at).toLocaleDateString() : '',
          candidate.interview_invite_sent_at ? new Date(candidate.interview_invite_sent_at).toLocaleDateString() : '',
          candidate.interview_invite_read_at ? new Date(candidate.interview_invite_read_at).toLocaleDateString() : '',
          candidate.application_date ? new Date(candidate.application_date).toLocaleDateString() : '',
          candidate.offer_sent ? 'Yes' : 'No',
          candidate.offer_status || '',
          candidate.offer_response_at ? new Date(candidate.offer_response_at).toLocaleDateString() : ''
        ]);
      });

      // Applied students
      cycle.applied_candidates.forEach(candidate => {
        csvData.push([
          cycle.year,
          'Applied',
          candidate.id,
          candidate.first_name && candidate.last_name ? `${candidate.first_name} ${candidate.last_name}` : `Candidate #${candidate.id}`,
          candidate.university || '',
          candidate.major || '',
          candidate.class_year || '',
          candidate.gpa || '',
          candidate.data_source || '',
          candidate.email || '',
          candidate.personal_email || '',
          candidate.shortlisted_at ? new Date(candidate.shortlisted_at).toLocaleDateString() : '',
          candidate.apply_invite_sent_at ? new Date(candidate.apply_invite_sent_at).toLocaleDateString() : '',
          candidate.apply_invite_read_at ? new Date(candidate.apply_invite_read_at).toLocaleDateString() : '',
          candidate.external_link_sent_at ? new Date(candidate.external_link_sent_at).toLocaleDateString() : '',
          candidate.external_link_read_at ? new Date(candidate.external_link_read_at).toLocaleDateString() : '',
          candidate.interview_invite_sent_at ? new Date(candidate.interview_invite_sent_at).toLocaleDateString() : '',
          candidate.interview_invite_read_at ? new Date(candidate.interview_invite_read_at).toLocaleDateString() : '',
          candidate.application_date ? new Date(candidate.application_date).toLocaleDateString() : '',
          candidate.offer_sent ? 'Yes' : 'No',
          candidate.offer_status || '',
          candidate.offer_response_at ? new Date(candidate.offer_response_at).toLocaleDateString() : ''
        ]);
      });

      // Matched students (Potential Candidates)
      cycle.matched_candidates.forEach(candidate => {
        csvData.push([
          cycle.year,
          'Matched',
          candidate.id,
          candidate.first_name && candidate.last_name ? `${candidate.first_name} ${candidate.last_name}` : `Candidate #${candidate.id}`,
          candidate.university || '',
          candidate.major || '',
          candidate.class_year || '',
          candidate.gpa || '',
          candidate.data_source || '',
          candidate.email || '', // Email for matched students
          candidate.personal_email || '',
          candidate.shortlisted_at ? new Date(candidate.shortlisted_at).toLocaleDateString() : '',
          candidate.apply_invite_sent_at ? new Date(candidate.apply_invite_sent_at).toLocaleDateString() : '',
          candidate.apply_invite_read_at ? new Date(candidate.apply_invite_read_at).toLocaleDateString() : '',
          candidate.external_link_sent_at ? new Date(candidate.external_link_sent_at).toLocaleDateString() : '',
          candidate.external_link_read_at ? new Date(candidate.external_link_read_at).toLocaleDateString() : '',
          candidate.interview_invite_sent_at ? new Date(candidate.interview_invite_sent_at).toLocaleDateString() : '',
          candidate.interview_invite_read_at ? new Date(candidate.interview_invite_read_at).toLocaleDateString() : '',
          candidate.application_date ? new Date(candidate.application_date).toLocaleDateString() : '',
          candidate.offer_sent ? 'Yes' : 'No',
          candidate.offer_status || '',
          candidate.offer_response_at ? new Date(candidate.offer_response_at).toLocaleDateString() : ''
        ]);
      });
    });

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(cell => {
        // Escape quotes and wrap in quotes if necessary
        const cellString = String(cell || '');
        if (cellString.includes(',') || cellString.includes('"') || cellString.includes('\n')) {
          return `"${cellString.replace(/"/g, '""')}"`;
        }
        return cellString;
      }).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename with recruiter name and date
    const today = new Date().toISOString().split('T')[0];
    const recruiterName = historyData.recruiter.name.replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('download', `${recruiterName}_interaction_history_${today}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderActionStatus = (candidate) => {
    const actions = [];
    
    if (candidate.shortlisted_at) {
      actions.push(
        <span key="shortlisted" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mr-1 mb-1">
          ★ Shortlisted {formatDate(candidate.shortlisted_at)}
        </span>
      );
    }
    
    if (candidate.apply_invite_sent_at) {
      actions.push(
        <span key="invite" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1">
          📧 Profile requested {formatDate(candidate.apply_invite_sent_at)}
          {candidate.apply_invite_read_at && <span className="ml-1">✓✓</span>}
        </span>
      );
    }
    
    if (candidate.external_link_sent_at) {
      actions.push(
        <span key="link" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-1 mb-1">
          🔗 Link Sent {formatDate(candidate.external_link_sent_at)}
          {candidate.external_link_read_at && <span className="ml-1">✓✓</span>}
        </span>
      );
    }
    
    if (candidate.interview_invite_sent_at) {
      actions.push(
        <span key="interview" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-1 mb-1">
          🎯 Interview {formatDate(candidate.interview_invite_sent_at)}
          {candidate.interview_invite_read_at && <span className="ml-1">✓✓</span>}
        </span>
      );
    }
    
    if (candidate.application_date) {
      actions.push(
        <span key="applied" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 mr-1 mb-1">
          ✅ Applied {formatDate(candidate.application_date)}
        </span>
      );
    }
    
    // Offer status
    if (candidate.offer_sent) {
      const offerColor = candidate.offer_status === 'accepted' ? 'bg-green-100 text-green-800' : 
                        candidate.offer_status === 'declined' ? 'bg-red-100 text-red-800' : 
                        'bg-orange-100 text-orange-800';
      
      const offerIcon = candidate.offer_status === 'accepted' ? '✅' : 
                       candidate.offer_status === 'declined' ? '❌' : 
                       '📧';
      
      const offerText = candidate.offer_status === 'accepted' ? 'Offer Accepted' : 
                       candidate.offer_status === 'declined' ? 'Offer Declined' : 
                       'Offer Sent';
      
      actions.push(
        <span key="offer" className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${offerColor} mr-1 mb-1`}>
          {offerIcon} {offerText} {candidate.offer_sent_at ? formatDate(candidate.offer_sent_at) : ''}
        </span>
      );
    }
    
    return <div className="flex flex-wrap">{actions}</div>;
  };

  const renderCandidateCard = (candidate, category) => {
    const isExpanded = expandedStudents.has(candidate.id);
    const isAnonymous = false; // Show real names for all candidates including Potential Candidates
    
    return (
      <div key={candidate.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {!isAnonymous && (
                <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
                  {candidate.headshot ? (
                    <img 
                      src={getImageUrl(candidate.headshot)} 
                      alt={`${candidate.first_name} ${candidate.last_name}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 font-semibold text-sm ${candidate.headshot ? 'hidden' : 'flex'}`}
                  >
                    {candidate.first_name ? candidate.first_name[0] : '?'}{candidate.last_name ? candidate.last_name[0] : ''}
                  </div>
                </div>
              )}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isAnonymous ? `Candidate #${candidate.id}` : `${candidate.first_name || ''} ${candidate.last_name || ''}`}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {candidate.university} • {candidate.major} • Class of: {candidate.class_year}
                </p>
                {candidate.gpa && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    GPA: {candidate.gpa}
                  </p>
                )}
                {candidate.data_source && (
                  <span className={`inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium ${
                    candidate.data_source === 'Self-reported'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                      : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  }`}>
                    {candidate.data_source === 'Self-reported' ? '📝 ' : '✓ '}{candidate.data_source}
                  </span>
                )}
              </div>
            </div>

            {/* Action Status */}
            <div className="mb-3">
              {renderActionStatus(candidate)}
            </div>

            {/* Expanded Details */}
            {isExpanded && !isAnonymous && candidate.shortlisted_at && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300">Email:</p>
                    <p className="text-gray-600 dark:text-gray-400">{candidate.email}</p>
                  </div>
                  {candidate.resume && (
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300">Resume:</p>
                      <a 
                        href={candidate.resume} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {candidate.resume_filename ? `View ${candidate.resume_filename}` : 'View Resume'}
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Detailed Timeline */}
                <div className="mt-4">
                  <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Interaction Timeline:</h5>
                  <div className="space-y-1 text-sm">
                    {candidate.shortlisted_at && (
                      <p>📌 Shortlisted: {formatDateTime(candidate.shortlisted_at)}</p>
                    )}
                    {candidate.apply_invite_sent_at && (
                      <p>📧 Profile Request Sent: {formatDateTime(candidate.apply_invite_sent_at)} 
                        {candidate.apply_invite_read_at && ` (Read: ${formatDateTime(candidate.apply_invite_read_at)})`}
                      </p>
                    )}
                    {candidate.external_link_sent_at && (
                      <p>🔗 External Link Sent: {formatDateTime(candidate.external_link_sent_at)}
                        {candidate.external_link_read_at && ` (Read: ${formatDateTime(candidate.external_link_read_at)})`}
                      </p>
                    )}
                    {candidate.interview_invite_sent_at && (
                      <p>🎯 Interview Invite Sent: {formatDateTime(candidate.interview_invite_sent_at)}
                        {candidate.interview_invite_read_at && ` (Read: ${formatDateTime(candidate.interview_invite_read_at)})`}
                      </p>
                    )}
                    {candidate.application_date && (
                      <p>✅ Student Applied: {formatDateTime(candidate.application_date)}</p>
                    )}
                    {candidate.offer_sent && (
                      <p>🎯 Offer Sent: {formatDateTime(candidate.offer_sent_at)}
                        {candidate.offer_status && candidate.offer_status !== 'pending' && candidate.offer_response_at && (
                          <span className={`ml-2 ${
                            candidate.offer_status === 'accepted' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            → {candidate.offer_status === 'accepted' ? 'Accepted' : 'Declined'}: {formatDateTime(candidate.offer_response_at)}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Expand/Collapse Button - Only for non-anonymous shortlisted students */}
          {!isAnonymous && candidate.shortlisted_at && (
            <button
              onClick={() => toggleStudentExpansion(candidate.id)}
              className="ml-4 px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
            >
              {isExpanded ? '▼ Less' : '▶ More'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCandidateSection = (title, candidates, emptyMessage, bgColor = "bg-gray-50", category = "") => {
    if (candidates.length === 0) {
      return (
        <div className={`${bgColor} dark:bg-gray-700 rounded-lg p-4 mb-4`}>
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">{title} (0)</h4>
          <p className="text-gray-500 dark:text-gray-400 text-sm">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className={`${bgColor} dark:bg-gray-700 rounded-lg p-4 mb-4`}>
        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">{title} ({candidates.length})</h4>
        {candidates.map(candidate => renderCandidateCard(candidate, category))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-8"></div>
            <div className="space-y-4">
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!historyData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Interaction History
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                {historyData.recruiter.name} • {historyData.recruiter.organization}
              </p>
            </div>
            {/* Mobile Layout - Buttons stacked vertically */}
            <div className="md:hidden space-y-3">
              <button
                onClick={exportToCSV}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </button>
              <button
                onClick={handleBackToDashboard}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                {isAdminMode ? 'Back to Admin' : isBoardLiaisonMode ? 'Back to Dashboard' : 'Back to Dashboard'}
              </button>
            </div>

            {/* Desktop Layout - Buttons side by side */}
            <div className="hidden md:flex gap-3">
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </button>
              <button
                onClick={handleBackToDashboard}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                {isAdminMode ? 'Back to Admin' : isBoardLiaisonMode ? 'Back to Dashboard' : 'Back to Dashboard'}
              </button>
            </div>
          </div>

          {/* Resume Packet Export (current cycle) */}
          {packetEnabled && !isBoardLiaisonMode && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Resume Packets — Current Cycle
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Compile candidate resumes into a single PDF plus a CSV of student data. Builds in the background — you can keep working and download when it's ready.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { key: 'shortlisted', label: 'Shortlisted' },
                  { key: 'favorited', label: 'Favorited' },
                  { key: 'applied', label: 'Applied' },
                ].map(({ key, label }) => {
                  const job = packetJobs[key];
                  const building = job && ['queued', 'running', 'starting'].includes(job.status);
                  return (
                    <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col gap-2">
                      <div className="font-medium text-gray-800 dark:text-gray-200">{label}</div>
                      {(!job || job.status === 'error') && (
                        <button
                          onClick={() => startPacket(key)}
                          className="px-3 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg"
                        >
                          Export {label} Packet
                        </button>
                      )}
                      {building && (
                        <button
                          disabled
                          className="px-3 py-2 text-sm bg-teal-400 text-white rounded-lg opacity-70 cursor-not-allowed"
                        >
                          Building…{job.resumes_merged ? ` ${job.resumes_merged} merged` : ''}
                        </button>
                      )}
                      {job && job.status === 'done' && (
                        <>
                          <a
                            href={job.download_url}
                            className="px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg text-center"
                          >
                            Download ({job.resumes_merged} resume{job.resumes_merged === 1 ? '' : 's'})
                          </a>
                          <button onClick={() => startPacket(key)} className="text-xs text-teal-600 hover:underline self-start">
                            Regenerate
                          </button>
                          {job.resumes_skipped > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {job.resumes_skipped} not merged (see manifest.txt)
                            </div>
                          )}
                        </>
                      )}
                      {job && job.status === 'error' && (
                        <div className="text-xs text-red-600">{job.error || 'Failed'}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Overall Statistics */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Overall Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-10 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {historyData.overall_stats.total_cycles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Cycles</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {historyData.overall_stats.total_shortlisted}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Shortlisted</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {historyData.overall_stats.total_interested}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Favorited You</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {historyData.overall_stats.total_applied}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Applied
                  <div className="text-xs text-gray-500 dark:text-gray-500">(from Shortlist)</div>
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {historyData.overall_stats.total_matched}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Matched</div>
                <div className="text-xs text-gray-500 dark:text-gray-500">(from Shortlist)</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {historyData.overall_stats.total_links_sent}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Links Sent</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {historyData.overall_stats.total_interviews_sent}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Interviews</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {historyData.overall_stats.total_offers_sent}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Offers Sent</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {historyData.overall_stats.total_offers_accepted}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Accepted</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                  {historyData.overall_stats.total_offers_declined}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Declined</div>
              </div>
            </div>
          </div>
        </div>

        {/* Cycles */}
        {historyData.cycles.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <p className="text-gray-500 dark:text-gray-400">No interaction history found.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {historyData.cycles.map((cycle) => {
              const isExpanded = expandedCycles.has(cycle.year);
              
              return (
                <div key={cycle.year} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
                  {/* Cycle Header */}
                  <div 
                    className="p-6 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => toggleCycleExpansion(cycle.year)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          {cycle.year}
                          {cycle.is_current && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                              Current
                            </span>
                          )}
                        </h2>
                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 text-sm">
                          <div><span className="font-medium">Total:</span> {cycle.stats.total_shortlisted}</div>
                          <div><span className="font-medium">Favorited You:</span> {cycle.stats.interested_count}</div>
                          <div>
                            <span className="font-medium">Applied:</span> {cycle.stats.applied_count}
                            <div className="text-xs text-gray-500 dark:text-gray-500">(from Shortlist)</div>
                          </div>
                          <div>
                            <span className="font-medium">Matched:</span> {cycle.stats.matched_count}
                            <div className="text-xs text-gray-500 dark:text-gray-500">(from Shortlist)</div>
                          </div>
                          <div><span className="font-medium">Links:</span> {cycle.stats.external_links_sent}</div>
                          <div><span className="font-medium">Interviews:</span> {cycle.stats.interviews_sent}</div>
                          <div><span className="font-medium">Offers:</span> {cycle.stats.offers_sent}</div>
                          <div><span className="font-medium text-green-600">Accepted:</span> {cycle.stats.offers_accepted}</div>
                          <div><span className="font-medium text-red-600">Declined:</span> {cycle.stats.offers_declined}</div>
                        </div>
                      </div>
                      <div className="text-gray-400">
                        {isExpanded ? '▼' : '▶'}
                      </div>
                    </div>
                  </div>

                  {/* Cycle Content */}
                  {isExpanded && (
                    <div className="p-6">
                      {/* Favorited You Section with Follow-up indicator */}
                      {cycle.interested_candidates.length > 0 ? (
                        <div className="bg-green-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">
                            Favorited You ({cycle.interested_candidates.length})
                          </h4>

                          {/* Students who favorited AND applied */}
                          {cycle.interested_candidates.filter(interested =>
                            cycle.applied_candidates.some(applied => applied.id === interested.id)
                          ).length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                ✅ Completed Application ({cycle.interested_candidates.filter(interested =>
                                  cycle.applied_candidates.some(applied => applied.id === interested.id)
                                ).length})
                              </h5>
                              {cycle.interested_candidates
                                .filter(interested =>
                                  cycle.applied_candidates.some(applied => applied.id === interested.id)
                                )
                                .map(candidate => renderCandidateCard(candidate, "interested_candidates"))}
                            </div>
                          )}

                          {/* Students who favorited but did NOT apply - Follow-up needed */}
                          {cycle.interested_candidates.filter(interested =>
                            !cycle.applied_candidates.some(applied => applied.id === interested.id)
                          ).length > 0 && (
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                              <h5 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                                🔴 Follow-up Needed - Application Not Completed ({cycle.interested_candidates.filter(interested =>
                                  !cycle.applied_candidates.some(applied => applied.id === interested.id)
                                ).length})
                              </h5>
                              {cycle.interested_candidates
                                .filter(interested =>
                                  !cycle.applied_candidates.some(applied => applied.id === interested.id)
                                )
                                .map(candidate => renderCandidateCard(candidate, "interested_candidates"))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-green-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">Favorited You (0)</h4>
                          <p className="text-gray-500 dark:text-gray-400 text-sm">No students have favorited you in this cycle.</p>
                        </div>
                      )}
                      
                      {renderCandidateSection(
                        "Applied Students",
                        cycle.applied_candidates.filter(applied =>
                          !cycle.interested_candidates.some(interested => interested.id === applied.id)
                        ),
                        "No shortlisted students have applied without favoriting you.",
                        "bg-blue-50",
                        "applied_candidates"
                      )}
                      
                      {renderCandidateSection(
                        "Potential Candidates",
                        cycle.matched_candidates,
                        "No potential candidates were shortlisted in this cycle.",
                        "bg-gray-50",
                        "matched_candidates"
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecruiterInteractionHistory;